import React, { Fragment, useEffect, useState } from 'react'
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem
} from '@mui/material'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { CheckboxWithLabel, Select, TextField } from 'formik-mui'
import { positionValidationSchema } from '../../types/positionConfig'
import { PositionSubZone } from '../../../../interfaces/Position/Position'
import { useStyles } from './styles'
import { apiCall, ApiConfig } from '../../../../config/api'
import { Zone } from '../../../../interfaces'
import { usePosition } from '../../context'
import positionBusiness from '../../actions/positionBusiness'
import positionActions from '../../actions/positionActions'
import { useTypePosition, useWarehouses, useZones } from '../../../../hooks'
import { useSubZones } from '../../../../hooks/useSubZones'
import { eTypePosition } from '../../../../enums/eTypePosition'

interface CreatePositionProps {
  skipPacking?: boolean
}

const initialValues = {
  id: 0,
  warehouseId: '' as unknown as number,
  positionName: '',
  positionDescription: '',
  subZoneId: undefined,
  zoneId: undefined,
  typePositionId: '' as unknown as number,
  active: true,
  externalStorage: false
}

export const CreatePosition: React.FC<CreatePositionProps> = () => {
  const [requiredZone, setRequiredZone] = useState<boolean>(false)

  const {
    zonesState: { zones },
    getZones
  } = useZones()

  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  const {
    subzonesState: { subzones },
    getSubZones
  } = useSubZones()

  const {
    typePositionsState: { typePositions },
    getTypePositions
  } = useTypePosition()

  const { state, dispatch } = usePosition()
  const {
    editableItem,
    typePositionDDL,
    allSubZones,
    zonesDDL,
    subZonesInZone
  } = state
  const { handleSubmit, handleSubmitEdit } = positionBusiness()
  const { setZoneDDL, setAllSubZones, setSubZonesInZone, saveTypePositionDDL } =
    positionActions()

  const classes = useStyles()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  useEffect(() => {
    if (zones?.length === 0) {
      getZones()
    }
  }, [])

  useEffect(() => {
    if (subzones?.length === 0) {
      getSubZones()
    }
  }, [])

  useEffect(() => {
    if (typePositions?.length === 0) {
      getTypePositions()
    }
  }, [])

  const handleSelectedWarehouseZones = (warehouseId: number, zones: Zone[]) => {
    if (warehouseId > 0) {
      const initialZones = [...zones!]

      const filteredZones = initialZones.filter(
        (x: Zone) => x.warehouseId === warehouseId
      )

      dispatch(setZoneDDL(filteredZones))
    }
  }

  const validateSelectedTypePosition = (typePosition: number) => {
    if (typePosition > 0) {
      const filteredTypePositions = typePositions?.filter(
        (x) => x.typePositionId === typePosition
      )

      const fieldValue =
        filteredTypePositions && filteredTypePositions.length > 0
          ? filteredTypePositions[0]?.requireZone
          : false

      setRequiredZone(fieldValue)
      return fieldValue
    }

    return false
  }

  useEffect(() => {
    apiCall('GET', `${ApiConfig.SubZoneApi}/GetDdlInfo`)
      .then((response) => response.data)
      .then(async (data) => {
        dispatch(setAllSubZones(data))
        refreshSubZonesInZone(data, editableItem?.zoneId)
      })
  }, [])

  useEffect(() => {
    apiCall('GET', `${ApiConfig.TypepositionApi}?pageSize=500`)
      .then((response) => response.data)
      .then((data) => {
        dispatch(saveTypePositionDDL(data))
      })
  }, [])

  const refreshSubZonesInZone = (
    arraySubZones: PositionSubZone[],
    zoneId: number | undefined
  ) => {
    const _subZones = arraySubZones.filter((row) => row.zoneId === zoneId)
    dispatch(setSubZonesInZone(_subZones))
  }

  useEffect(() => {
    handleSelectedWarehouseZones(editableItem?.warehouseId!, zones!)
  }, [editableItem])

  const handleZoneChange = (
    value: number,
    setFieldValue: (field: string, value: any) => void,    
    zonesDDL: Zone[]
  ) => {
    setFieldValue('zoneId', value)
    setFieldValue('subZoneId', undefined)
    refreshSubZonesInZone(allSubZones, value) 
    const selectedZone = zonesDDL.find((z) => z.zoneId === Number(value))
    const isExternal = selectedZone ? !!selectedZone.externalStorage : false
    setFieldValue('externalStorage', isExternal)
  }

  const shouldShowTypePosition = (
    isExternalZone: boolean,
    typePositionId: number,
    isEditable: boolean,
  ): boolean => {
    // Valida si se trata de una posición de tipo reserva 
    const isReserva = typePositionId === eTypePosition.Reserva
    // Se devuelve como verdadero para caso externo y reserva o diferente de reserva y editable.
    return (isExternalZone && isReserva && isEditable) || (!isReserva && isEditable)
  }

  return (
    <Fragment>
      <Formik
        initialValues={
          editableItem && editableItem.id ? editableItem : initialValues
        }
        onSubmit={async (values) =>
          new Promise(() =>
            editableItem ? handleSubmitEdit(values) : handleSubmit(values)
          )
        }
        validationSchema={positionValidationSchema(requiredZone)}
      >
        {({ isSubmitting, isValid, values, setFieldValue }) => (
          <Form autoComplete='off'>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  fullWidth
                  name='positionName'
                  component={TextField}
                  label='Nombre Posicion'
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} fullWidth>
                  <InputLabel id='selectedWarehouse'>Almacén</InputLabel>
                  <Field
                    fullWidth
                    labelId='selectedWarehouse'
                    id='warehouseId'
                    name='warehouseId'
                    component={Select}
                    value={values.warehouseId}
                    onChange={(e: any) => {
                      const { value } = e.target
                      setFieldValue('warehouseId', value)
                      handleSelectedWarehouseZones(value, zones!)
                      setFieldValue('zoneId', undefined)
                      setFieldValue('subZoneId', undefined)
                      setFieldValue('externalStorage', value)                      
                    }}
                  >
                    {warehouses &&
                      warehouses.map(({ id, warehouseName }, index) => {
                        return (
                          <MenuItem key={index} value={id}>
                            {warehouseName}
                          </MenuItem>
                        )
                      })}
                  </Field>
                  <p
                    style={{
                      color: '#f44336',
                      fontSize: '13px'
                    }}
                  >
                    <ErrorMessage name='warehouseId' />
                  </p>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} fullWidth>
                  <InputLabel id='selectedZone'>Zona</InputLabel>
                  <Field
                    fullWidth
                    labelId='selectedZone'
                    id='zoneId'
                    name='zoneId'
                    component={Select}
                    value={values.zoneId}
                    onChange={async (e: any) => {
                      handleZoneChange(e.target.value, setFieldValue, zonesDDL)
                    }}
                  >
                    <MenuItem key={0} value={undefined}>
                      Sin Zona
                    </MenuItem>
                    {zonesDDL.map(({ zoneId, zoneName }, index) => {
                      return (
                        <MenuItem key={index} value={zoneId}>
                          {zoneName}
                        </MenuItem>
                      )
                    })}
                  </Field>
                  <p
                    style={{
                      color: '#f44336',
                      fontSize: '13px'
                    }}
                  >
                    <ErrorMessage name='zoneId' />
                  </p>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} fullWidth>
                  <InputLabel id='selectedSubZone'>SubZona</InputLabel>
                  <Field
                    fullWidth
                    labelId='selectedSubZone'
                    id='subZoneId'
                    name='subZoneId'
                    component={Select}
                    value={values.subZoneId}
                  >
                    <MenuItem key={0} value={undefined}>
                      Sin SubZona
                    </MenuItem>
                    {subZonesInZone.map(({ subZoneId, subZoneName }, index) => {
                      return (
                        <MenuItem key={index} value={subZoneId}>
                          {subZoneName}
                        </MenuItem>
                      )
                    })}
                  </Field>
                  <p
                    style={{
                      color: '#f44336',
                      fontSize: '13px'
                    }}
                  >
                    <ErrorMessage name='subZoneId' />
                  </p>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formControl} fullWidth>
                  <InputLabel id='selectedTypePosition'>
                    Tipo Posición
                  </InputLabel>
                  <Field
                    fullWidth
                    labelId='selectedTypePosition'
                    id='typePositionId'
                    name='typePositionId'
                    component={Select}
                    value={values.typePositionId}
                    onChange={async (e: any) => {
                      const { value } = e.target
                      validateSelectedTypePosition(value)
                      setFieldValue('typePositionId', value)
                    }}
                  >
                    {typePositionDDL.items.map(
                      ({
                          typePositionId,
                          typePositionDescription,
                          canCreatePosition
                        }, index) => {
                        const shouldShow = shouldShowTypePosition(
                          values.externalStorage,
                          typePositionId,
                          canCreatePosition
                        )

                        return shouldShow ? (
                          <MenuItem key={index} value={typePositionId}>
                            {typePositionDescription}
                          </MenuItem>
                        ) : null
                      }
                    )}
                  </Field>
                  <p
                    style={{
                      color: '#f44336',
                      fontSize: '13px'
                    }}
                  >
                    <ErrorMessage name='typePositionId' />
                  </p>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Field
                  fullWidth
                  name='positionDescription'
                  component={TextField}
                  label='Descripción Posición'
                  multiline
                  rows={3}
                  variant='outlined'
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={2}>
              <Field
                name='active'
                component={CheckboxWithLabel}
                Label={{ label: 'Activo' }}
                type='checkbox'
                color='default'
                checked={values.active}
                onChange={async (e: any) => {
                  const { checked } = e.target
                  setFieldValue('active', checked)
                }}
              />
            </Grid>

            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Button
                  disabled={isSubmitting || !isValid}
                  fullWidth
                  type='submit'
                  variant='contained'
                  color={isValid ? 'primary' : 'secondary'}
                  style={{
                    marginTop: '10px'
                  }}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size='0.9rem' />
                    ) : undefined
                  }
                >
                  {isSubmitting ? '' : 'Guardar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
}
